import React, { useState, useEffect } from "react";
import { Button, MenuButton } from "components/button";
import { Input } from "components/input";
import { ContainerWithSidebar } from "../../../components/layout/index";
import axios from "axios";
import FileSaver from "file-saver";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "styles/theme";

import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

import "./index.scss";
import Switch from "rc-switch";
import SendPurchaseOrder from "./components/SendPurchaseOrder";
import { groupPosByOrder } from "../utils/groupPosByOrder";
import { set } from "lodash";
import { resolveHeritageSubmission } from "./components/sendSubmissionState";

// Custom container that accounts for sticky tab bar
const PurchasingContainerWithSidebar = styled(ContainerWithSidebar)`
  > div:first-child {
    top: calc(${theme.height.navigation} + 45px); // navigation + tab bar height
    height: calc(100vh - ${theme.height.navigation} - 45px - 20px); // subtract extra buffer for margins/padding
  }
`;

const Send = (props) => {
  const { t } = useTranslation();
  const tBase = "views.purchasing.send.index";
  const tr = (key) => t(`${tBase}.${key}`);

  const [sendList, setSendList] = useState([]);
  const [currentPo, setCurrentPo] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [sendViaEmail, setSendViaEmail] = useState(true);
  const [sendViaApi, setSendViaApi] = useState(false);
  const [open, setOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);

  // After a submit the list is refetched; the open PO must pick up its new
  // submission summary or the button row would still offer Submit.
  useEffect(() => {
    if (!currentPo) return;
    const fresh = (sendList || []).find((po) => po.id === currentPo.id);
    if (fresh && fresh !== currentPo) setCurrentPo(fresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendList]);

  const submission = resolveHeritageSubmission(currentPo);

  const fetchSend = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/fetchSend`
    );

    setSendList(response.data.sendResult);
    const details = response.data.companyDetails[0];
    setCompanyDetails(details);
    
    // Fetch company logo - check logo_url first (from database), then fetch signed URL from S3
    if (details?.logo_url) {
      // Use logo_url if provided in companyDetails
      setCompanyLogo(details.logo_url);
    } else if (details?.companyLogo) {
      // Fallback to companyLogo if available
      setCompanyLogo(details.companyLogo);
    } else {
      // Fetch signed URL from company settings endpoint (matches PDF generation logic)
      try {
        const logoResponse = await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/companySettings/company-logo`
        );
        if (logoResponse.data) {
          setCompanyLogo(logoResponse.data);
        }
      } catch (error) {
        // Logo fetch failed, will just not display logo
        console.warn("Failed to fetch company logo:", error);
      }
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getOrderPdf = async (id) => {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/getOrderPdf/${id}`,
      { responseType: "blob" }
    );

    FileSaver.saveAs(
      response.data,
      `${new Date().getFullYear()}-${new Date().getMonth()}-${new Date().getDate()}_${currentPo.po
      }_${currentPo.vendor_name || currentPo.vendor}`
    );
  };

  const handleSend = async () => {
    let po = currentPo;
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/emailSend`,
      {
        emailTo,
        currentPo,
        sendViaEmail,
        sendViaApi,
      }
    );

    setCurrentPo(null);
    if (response.status === 200) {
      po.email_timestamp = new Date().toLocaleDateString();
    }
    setCurrentPo(po);
  };

  const updateSent = async (id) => {
    await axios.put(
      `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/purchasing/updateSent`,
      {
        poId: id,
      }
    );

    setCurrentPo(null);
    props.fetchCounts();
    fetchSend();
  };

  const handleChangePo = (po) => {
    sendList.forEach((item) => {
      if (item.id === po) {
        setCurrentPo(item);
      }
    });
  };

  const sumItemsPrices = (items) => {
    let sum = 0;

    if (items) {
      items.forEach((item) => {
        sum += item.price * item.quantity;
      });
    }

    return sum;
  };

  useEffect(() => {
    fetchSend();
  }, []);

  useEffect(() => {
    if (
      currentPo &&
      currentPo.hasOwnProperty("email_address") &&
      currentPo.email_address
    ) {
      setEmailTo(currentPo.email_address);
    } else {
      setEmailTo("");
    }
  }, [currentPo]);

  if (!companyDetails) return null;

  return (
    <PurchasingContainerWithSidebar>
      <div className="purchasing-sidebar" style={{ backgroundColor: "#edf1f4" }}>
        <div className="review-menu-title">{tr("Ready to Send")}</div>
        <hr />
        {/* #41 — one row per order; the vendors live in tabs on the right. */}
        {sendList &&
          groupPosByOrder(sendList).map((group) => {
            const first = group.pos[0];
            const isActive = group.pos.some((po) => po.id === currentPo?.id);
            return (
              <MenuButton
                style={{
                  textAlign: "left",
                  marginTop: 3,
                  marginBottom: 3,
                  fontSize: "90%",
                  fontWeight: isActive ? 700 : 400,
                }}
                key={group.key}
                onClick={() => handleChangePo(first.id)}
              >
                <span>
                  {group.orderId != null
                    ? `${tr("Order")} #${group.orderId}`
                    : first.po}
                </span>
                <span>
                  {" ("}
                  {group.pos.length > 1
                    ? tr("vendors count").replace("{{count}}", String(group.pos.length))
                    : first.vendor_name || first.vendor}
                  {")"}
                </span>
              </MenuButton>
            );
          })}
      </div>
      <div>
        {currentPo ? (
          <>
            {(() => {
              const activeGroup = groupPosByOrder(sendList || []).find((group) =>
                group.pos.some((po) => po.id === currentPo.id)
              );
              if (!activeGroup || activeGroup.pos.length < 2) return null;
              return (
                <div className="order-vendor-tabs">
                  {activeGroup.pos.map((po) => (
                    <button
                      key={po.id}
                      type="button"
                      className={
                        po.id === currentPo.id
                          ? "order-vendor-tab order-vendor-tab-active"
                          : "order-vendor-tab"
                      }
                      onClick={() => handleChangePo(po.id)}
                    >
                      {po.vendor_name || po.vendor} ({po.po})
                    </button>
                  ))}
                </div>
              );
            })()}
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <SendPurchaseOrder
                orderId={currentPo.id}
                sendViaEmail={sendViaEmail}
                setSendViaEmail={setSendViaEmail}
                sendViaApi={sendViaApi}
                setSendViaApi={setSendViaApi}
                emailTo={emailTo}
                onSuccess={() => {
                  handleClose();
                  fetchSend();
                }}
                onClose={handleClose}
              />
            </Dialog>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginRight: 45,
                marginTop: 10,
                justifyContent: "flex-end",
              }}
            >
              {currentPo.email_timestamp ? (
                <div className="email-label">
                  <div>
                    {tr("Email Sent")}:{" "}
                    {new Date(currentPo.email_timestamp).toLocaleDateString()}
                  </div>
                </div>
              ) : null}
              <Input
                style={{ width: 300 }}
                placeholder={tr("email")}
                value={emailTo}
                onChange={(e) => {
                  setEmailTo(e.target.value);
                }}
              />
              {submission.label ? (
                <div
                  className="email-label"
                  style={{ marginRight: 10 }}
                  title={submission.detail || undefined}
                  data-testid="heritage-submission-status"
                >
                  <div>
                    {tr("Heritage status")}: {tr(submission.label)}
                    {submission.partial && submission.detail
                      ? ` (${submission.detail})`
                      : ""}
                  </div>
                </div>
              ) : null}
              <Button
                primary={currentPo.email_timestamp || submission.submitted ? false : true}
                onClick={handleClickOpen}
                style={{ marginLeft: 10 }}
                // Once every branch group has its Heritage order there is
                // nothing left to submit; a partial split keeps the button so
                // the retry can finish the missing branch.
                disabled={submission.submitted}
                title={submission.submitted ? tr("Already submitted to Heritage") : undefined}
              >
                {/* H8 — an integrated vendor's order is submitted through the
                    API, and the button says so; Send stays for email vendors. */}
                {currentPo.is_heritage ? tr("Submit") : tr("Send")}
              </Button>

              <Button
                onClick={() => getOrderPdf(currentPo.id)}
                style={{ marginLeft: 10 }}
              >
                {tr("Download")}
              </Button>

              <Button
                onClick={() => {
                  updateSent(currentPo.id);
                }}
                style={{ marginLeft: 10 }}
                primary={currentPo.email_timestamp ? true : false}
              >
                {tr("Move to Receiving")}
              </Button>
            </div>
            <div
              style={{
                border: "1px solid black",
                width: 768,
                minHeight: 960,
                margin: "10px auto",
              }}
            >
              <div
                id="po"
                className="po-body"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 960,
                  width: 768,
                  margin: "10px auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {companyLogo && (
                      <img 
                        src={companyLogo} 
                        style={{ width: 75, height: 75 }} 
                        alt="company-logo" 
                      />
                    )}
                    <div style={{ fontSize: "70%" }}>
                      {companyDetails.name && <div>{companyDetails.name}</div>}
                      {companyDetails.address && (
                        <div>{companyDetails.address}</div>
                      )}
                      <div>{`${companyDetails.city}, ${companyDetails.state} ${companyDetails.zipcode}`}</div>
                      {companyDetails.phone && (
                        <div>{companyDetails.phone}</div>
                      )}
                      {companyDetails.email && (
                        <div>{companyDetails.email}</div>
                      )}
                    </div>
                  </div>

                  <div className="po-info-box">
                    <div style={{ fontWeight: "bold", fontSize: "150%" }}>
                      {tr("Purchase Order")}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>{tr("DATE")}</div>
                        <div
                          style={{
                            border: "1px solid black",
                            padding: "2px 10px",
                            width: 100,
                            textAlign: "center",
                          }}
                        >
                          {new Date(currentPo.timestamp).toLocaleDateString()}
                        </div>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>PO #</div>
                        <div
                          style={{
                            border: "1px solid black",
                            padding: "2px 10px",
                            width: 100,
                            textAlign: "center",
                            borderTop: 0,
                          }}
                        >
                          {currentPo.po}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="vendor-box">
                    <div>
                      <div className="vendor-header">{tr("Vendor")}</div>
                      <div className="vendor-details">
                        <div>
                          {currentPo.vendor_name
                            ? currentPo.vendor_name
                            : currentPo.vendor}
                        </div>
                        {currentPo.street_address ? (
                          <>
                            <div>{currentPo.street_address}</div>
                            <div>{currentPo.address_line_2}</div>
                            <div>
                              {currentPo.city}, {currentPo.vendor_state}{" "}
                              {currentPo.zip_code}
                            </div>
                          </>
                        ) : null}
                        {currentPo.email_address ? (
                          <div>{currentPo.email_address}</div>
                        ) : null}
                        {currentPo.phone_number ? (
                          <div>{currentPo.phone_number}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="ship-to-box">
                    <div className="ship-to-header">{tr("Ship To")}</div>
                    <div className="ship-to-details">
                      {companyDetails.name && <div>{companyDetails.name}</div>}
                      {companyDetails.address && (
                        <div>{companyDetails.address}</div>
                      )}
                      <div>{`${companyDetails.city}, ${companyDetails.state} ${companyDetails.zipcode}`}</div>
                    </div>
                  </div>
                </div>

                <div className="shipping-box">
                  <div className="shipping-header">Shipping Method</div>
                  <div className="shipping-details">
                    {currentPo.shipping_method}
                  </div>
                </div>

                <div className="products-box">
                  <div className="products-header">
                    <div className="products-header-col-1">
                      {tr("Product Name/Description")}
                    </div>
                    <div className="products-header-col-2">
                      {tr("Quantity")}
                    </div>
                    <div className="products-header-col-3">
                      {tr("Unit Price")}
                    </div>
                    <div className="products-header-col-4">Total</div>
                  </div>

                  {currentPo.items?.map((item) => {
                    return (
                      <div className="products-details">
                        <div className="products-body-col-1">
                          <div>
                            {`(${item.pc_name}) ${item.category_name}: ${item.item_name}`}
                          </div>
                          <div>
                            Vendor ID:{" "}
                            {item.vendor_part_number ||
                              item.model ||
                              item.sku ||
                              0}
                          </div>
                        </div>
                        <div className="products-body-col-2">
                          {item.quantity}
                        </div>

                        <div className="products-body-col-3">
                          {item.price && item.price > 0
                            ? (item.price / 100).toLocaleString("en-us", {
                              style: "currency",
                              currency: "usd",
                            })
                            : 0}
                        </div>
                        <div className="products-body-col-4">
                          {item.price && item.price > 0
                            ? (
                              (item.price * item.quantity) /
                              100
                            ).toLocaleString("en-us", {
                              style: "currency",
                              currency: "usd",
                            })
                            : 0}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    marginTop: 20,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="notes-box">
                    <div className="notes-header">
                      {tr("Notes/Instructions")}
                    </div>
                    <div className="notes-details">
                      {currentPo.notes || ""}
                    </div>
                  </div>

                  <div className="totals-box">
                    <div className="totals-row">
                      <div>{tr("Subtotal")}</div>
                      <div>
                        {(sumItemsPrices(currentPo.items) / 100).toLocaleString(
                          "en-us",
                          {
                            style: "currency",
                            currency: "usd",
                          }
                        )}
                      </div>
                    </div>
                    <div className="totals-row">
                      <div>{tr("Discount")} (-)</div>
                      <div>
                        {currentPo.discount > 0
                          ? (currentPo.discount / 100).toLocaleString("en-us", {
                            style: "currency",
                            currency: "usd",
                          })
                          : "$0.00"}
                      </div>
                    </div>
                    <div className="totals-row">
                      <div>{tr("Sales Tax")}</div>
                      <div>$0.00</div>
                    </div>
                    <div className="totals-row">
                      <div>{tr("Shipping")}</div>
                      <div>
                        {currentPo.shipping_cost > 0
                          ? (currentPo.shipping_cost / 100).toLocaleString(
                            "en-us",
                            {
                              style: "currency",
                              currency: "usd",
                            }
                          )
                          : 0}
                      </div>
                    </div>
                    <div
                      className="totals-row"
                      style={{ fontWeight: "bolder" }}
                    >
                      <div>{tr("Grand Total")}</div>
                      <div>
                        {(
                          (sumItemsPrices(currentPo.items) -
                            parseInt(currentPo.discount || 0) +
                            parseInt(currentPo.shipping_cost || 0)) /
                          100
                        ).toLocaleString("en-us", {
                          style: "currency",
                          currency: "usd",
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </PurchasingContainerWithSidebar>
  );
};

export default Send;
